import React, { useEffect, useRef, useState } from 'react';
import { Box, Popper, Typography } from '@mui/material';

import { HaloEffect } from './HaloEffect';
import { TypewriterAnimation } from './TypewriterAnimation';

// Animation and timing constants (in milliseconds)
const POPOVER_SLIDE_DURATION = 500;
const POPOVER_DELAY = 2000;
const AUTO_DISMISS_DELAY = 3000;
const HALO_PERSIST_DELAY = 3000;

interface NewNotificationProps {
  children: React.ReactNode;
  enabled?: boolean;
  compact?: boolean;
  messages?: string[];
  showPopoverDelay?: number;
  hidePopoverAfter?: number;
  onShow?: () => void;
}

export const NewNotification: React.FC<NewNotificationProps> = ({
  children,
  enabled = false,
  compact,
  messages = [],
  showPopoverDelay = POPOVER_DELAY,
  hidePopoverAfter,
  onShow,
}) => {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [haloEnabled, setHaloEnabled] = useState(false);
  const [hasCalledOnShow, setHasCalledOnShow] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  // Reset states when disabled
  useEffect(() => {
    if (!enabled) {
      setHasCalledOnShow(false);
    }
  }, [enabled]);

  useEffect(() => {
    if (enabled && messages.length > 0) {
      // Reset exit state when re-enabling
      setIsExiting(false);
      // Enable halo immediately
      setHaloEnabled(true);

      // Show popover after delay
      const showTimer = setTimeout(() => {
        setPopoverOpen(true);
      }, showPopoverDelay);

      // Optionally hide popover after specified duration
      let hideTimer: NodeJS.Timeout;
      if (hidePopoverAfter) {
        hideTimer = setTimeout(() => {
          setIsExiting(true);
          // Give time for exit animation before closing
          setTimeout(() => {
            setPopoverOpen(false);
          }, POPOVER_SLIDE_DURATION);
        }, showPopoverDelay + hidePopoverAfter);
      }

      return () => {
        clearTimeout(showTimer);
        if (hideTimer) clearTimeout(hideTimer);
      };
    } else {
      setPopoverOpen(false);
      setIsExiting(false);
      setHaloEnabled(false);
    }
  }, [enabled, messages, showPopoverDelay, hidePopoverAfter]);

  // Call onShow when popover actually opens (only once)
  useEffect(() => {
    if (popoverOpen && onShow && !hasCalledOnShow) {
      onShow();
      setHasCalledOnShow(true);
    }
  }, [popoverOpen, onShow, hasCalledOnShow]);

  // Disable halo 3 seconds after popover closes
  useEffect(() => {
    if (!popoverOpen && haloEnabled) {
      const haloTimer = setTimeout(() => {
        setHaloEnabled(false);
      }, HALO_PERSIST_DELAY);
      return () => clearTimeout(haloTimer);
    }
  }, [popoverOpen, haloEnabled]);

  // Note: Acknowledgment now happens via button click in parent component
  // keeping isAcknowledged state for potential future use

  const handleAnimationComplete = () => {
    // Auto-dismiss 3 seconds after typewriter completes
    if (!hidePopoverAfter) {
      setTimeout(() => {
        setIsExiting(true);
        // Give time for exit animation
        setTimeout(() => {
          setPopoverOpen(false);
        }, POPOVER_SLIDE_DURATION);
      }, AUTO_DISMISS_DELAY);
    }
  };

  return (
    <>
      <Box 
        ref={anchorRef} 
        sx={{ display: 'inline-flex', position: 'relative' }}
      >
        <HaloEffect enabled={haloEnabled} color="#D91E6C" duration={2} compact={compact}>
          {children}
        </HaloEffect>
      </Box>
      <Popper
        open={popoverOpen && messages.length > 0}
        anchorEl={anchorRef.current}
        placement="top"
        style={{ zIndex: 9999 }} // High z-index to appear above toolbar
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [0, 16], // Move bubble up from button
            },
          },
        ]}
      >
        <Box
          sx={{
            position: 'relative',
            minWidth: 'max-content',
            animation: isExiting
              ? `slideOut ${POPOVER_SLIDE_DURATION}ms cubic-bezier(0.68, -0.55, 0.265, 1.55)`
              : popoverOpen
                ? `slideInBounce ${POPOVER_SLIDE_DURATION}ms cubic-bezier(0.68, -0.55, 0.265, 1.55)`
                : 'none',
            '@keyframes slideInBounce': {
              '0%': {
                transform: 'translateY(-40px)',
                opacity: 0,
              },
              '40%': {
                transform: 'translateY(10px)',
                opacity: 1,
              },
              '60%': {
                transform: 'translateY(-5px)',
              },
              '80%': {
                transform: 'translateY(3px)',
              },
              '100%': {
                transform: 'translateY(0)',
              },
            },
            '@keyframes slideOut': {
              '0%': {
                transform: 'translateY(0)',
                opacity: 1,
              },
              '100%': {
                transform: 'translateY(-100px)',
                opacity: 0,
              },
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: '-8px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: 0,
              height: 0,
              borderLeft: '8px solid transparent',
              borderRight: '8px solid transparent',
              borderTop: '8px solid #2c2c2c',
              zIndex: 1,
            },
          }}
        >
          {/* Invisible placeholder to establish maximum width */}
          <Typography
            variant="body2"
            sx={{
              visibility: 'hidden',
              fontSize: '14px',
              lineHeight: 1.4,
              whiteSpace: 'nowrap',
              padding: '20px 24px',
            }}
          >
            <Box component="span" sx={{ fontWeight: 600, mr: 0.5 }}>
              New:
            </Box>
            <Box component="span" sx={{ mr: 0.5 }}>
              Generate a
            </Box>
            <Box
              component="span"
              sx={{ borderBottom: '1px solid transparent', fontWeight: 600, pb: 0.25 }}
            >
              Court-Ordered Treatment Documentationww
            </Box>
            <Box component="span">?</Box>
          </Typography>

          {/* Centered black bubble that grows/shrinks */}
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              backgroundColor: '#2c2c2c',
              color: 'white',
              borderRadius: '12px',
              padding: '20px 24px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.25)',
            }}
          >
            <TypewriterAnimation messages={messages} onComplete={handleAnimationComplete} />
          </Box>
        </Box>
      </Popper>
    </>
  );
};
